export const b2bPageData = {
  stats: [
    { value: "50%", label: "Order Volume Growth" },
    { value: "60%", label: "Faster Procurement Cycles" },
    { value: "35%", label: "Cost Reduction" },
    { value: "10,000+", label: "Verified Suppliers" },
  ],
  statsAccent: "from-indigo-600 to-blue-700",

  challengeTitle: "Traditional B2B Trading Is Costing You More Than You Know",
  challengeSubtitle:
    "Fragmented procurement, opaque pricing, and manual processes drain revenue and slow growth.",
  challenges: [
    {
      icon: "🗂️",
      title: "Fragmented Supplier Portals",
      description:
        "Navigating dozens of separate supplier websites, spreadsheets, and email chains wastes hours every week.",
    },
    {
      icon: "💸",
      title: "No Pricing Transparency",
      description:
        "Without market benchmarks, buyers overpay and suppliers undercut. Everyone loses without real-time pricing data.",
    },
    {
      icon: "📄",
      title: "Paper-Based Purchase Orders",
      description:
        "Manual PO creation, approval chains, and physical paperwork slow procurement to a crawl and introduce errors.",
    },
    {
      icon: "🔍",
      title: "Supplier Discovery Is Slow",
      description:
        "Finding new, qualified suppliers for new product categories takes weeks of research, outreach, and vetting.",
    },
    {
      icon: "📦",
      title: "Zero Fulfillment Visibility",
      description:
        "Once an order is placed, buyers have no visibility into production, shipping, or delivery status.",
    },
    {
      icon: "🤝",
      title: "Weak Supplier Relationships",
      description:
        "No performance data means you can't objectively evaluate suppliers or build loyalty with the best ones.",
    },
  ],
  theme: {
    challengeBg: "bg-slate-900",
    challengeTitle: "text-white",
    challengeSubtitle: "text-slate-400",
    challengeCard: "bg-slate-800/60",
    challengeBorder: "border-slate-700",
    badgeBg: "bg-indigo-50 text-indigo-700 border-indigo-100",
    solutionIconBg: "bg-indigo-50",
    solutionIconColor: "text-indigo-600",
    solutionAccent: "from-indigo-600 to-blue-600",
  },

  solutionTitle: "One Intelligent Marketplace. Infinite Trading Possibilities.",
  solutionSubtitle:
    "EkkBaz AI connects buyers and suppliers with AI-powered pricing, automated ordering, and real-time fulfillment tracking.",
  solutions: [
    {
      icon: "🏪",
      title: "Digital Supplier Marketplace",
      description:
        "Browse thousands of verified suppliers across every category. Compare, filter, and contact — all in one platform.",
    },
    {
      icon: "💰",
      title: "Real-Time Pricing Intelligence",
      description:
        "AI benchmarks market prices continuously so buyers always know if they're getting a good deal — and suppliers price competitively.",
    },
    {
      icon: "🤖",
      title: "AI Deal Recommendations",
      description:
        "The AI engine recommends the best deals based on your buying history, supplier performance, and current market prices.",
    },
    {
      icon: "📱",
      title: "Digital Order Management",
      description:
        "Place, approve, and track purchase orders digitally. No paper, no email chains — just clean, fast procurement.",
    },
    {
      icon: "📊",
      title: "Supplier Performance Scoring",
      description:
        "Every supplier is scored on delivery reliability, quality, and pricing. Always know who your best partners are.",
    },
    {
      icon: "🚚",
      title: "End-to-End Fulfillment Tracking",
      description:
        "Track every order from placement to delivery with real-time status updates and automated milestone notifications.",
    },
    {
      icon: "🌐",
      title: "Global Supplier Network",
      description:
        "Access verified regional and international suppliers instantly — expanding your sourcing options without the legwork.",
    },
    {
      icon: "📈",
      title: "Spend Analytics Dashboard",
      description:
        "Understand exactly where your procurement budget goes, identify savings opportunities, and optimise every category.",
    },
    {
      icon: "🔒",
      title: "Verified Buyer & Seller Trust",
      description:
        "All marketplace participants are verified and rated — ensuring safe, reliable transactions every time.",
    },
  ],

  howItWorksTitle: "Start Trading Smarter in 3 Simple Steps",
  howItWorksSteps: [
    {
      icon: "✅",
      title: "Create Your Profile",
      description:
        "Sign up, verify your business, and set your product categories or procurement needs in minutes.",
    },
    {
      icon: "🔎",
      title: "Discover & Compare",
      description:
        "Browse AI-recommended suppliers, compare prices, and request quotes — all in one place.",
    },
    {
      icon: "📦",
      title: "Order Digitally",
      description:
        "Place and approve purchase orders digitally with automated workflows and full fulfillment tracking.",
    },
    {
      icon: "📈",
      title: "Grow Faster",
      description:
        "Use AI insights to optimise sourcing, reduce costs, and expand your trading network continuously.",
    },
  ],

  outcomeTitle: "What Our Marketplace Members Achieve",
  outcomeSubtitle: "Measurable outcomes from businesses trading on EkkBaz AI.",
  outcomes: [
    {
      value: "50%",
      metric: "Order Volume Growth",
      description:
        "Average growth in order volume within six months of joining the marketplace — driven by better supplier access and AI recommendations.",
    },
    {
      value: "60%",
      metric: "Faster Procurement Cycles",
      description:
        "Procurement cycle time reduction from weeks to days — thanks to digital ordering and instant supplier connections.",
    },
    {
      value: "35%",
      metric: "Procurement Cost Savings",
      description:
        "Average cost reduction achieved through AI pricing intelligence and competitive multi-supplier comparisons.",
    },
  ],
  outcomeImage: "/assets/images/products/b2b2.png",

  ctaTitle: "Join Thousands of Businesses Trading Smarter",
  ctaSubtitle:
    "Get access to the EkkBaz AI B2B Marketplace and start sourcing from verified suppliers with AI-powered pricing intelligence today.",
  ctaLabel: "Get Started Free",
  ctaLink: "https://outlook.office.com/book/EkkBazInvestment@EkkBaz.com/?ismsaljsauthenabled",
};

export const b2bData = [
  {
    id: 1,
    extraLabel: "The Challenge",
    userType: "Traditional B2B Trading Is Inefficient, Opaque, and Costly",
    description:
      "Businesses waste enormous resources on fragmented B2B procurement — navigating multiple supplier portals, negotiating without market data, managing paper-based orders, and missing out on better deals. The result: higher costs, slower cycles, and missed growth opportunities.",
    banner: "/assets/images/products/b2b1.png",
  },
  {
    id: 2,
    extraLabel: "Our Solution",
    userType: "The Intelligent B2B Marketplace That Connects, Automates, and Optimizes",
    description:
      "EkkBaz AI's B2B Marketplace brings buyers and suppliers onto one digital platform powered by AI. Suppliers list products with real-time pricing; buyers compare multiple offers instantly, place orders digitally, and track fulfillment end-to-end. AI recommends the best deals based on your buying history, price benchmarks, and supplier performance scores.",
    banner: "/assets/images/products/b2b2.png",
  },
  {
    id: 3,
    extraLabel: "The Outcome",
    userType: "Grow Revenue 50% Faster with AI-Powered Marketplace Intelligence",
    description:
      "Businesses on EkkBaz's B2B Marketplace grow order volume by an average of 50%, reduce procurement cycle time by 60%, and make purchasing decisions backed by real-time AI pricing intelligence — achieving the best deals from the best suppliers, automatically.",
    banner: "/assets/images/products/b2b3.png",
  },
];

export const b2bFAQs = [
  {
    question: "What is EkkBaz's B2B Marketplace?",
    answer:
      "EkkBaz's B2B Marketplace is an AI-powered digital trading platform where businesses can buy and sell products at scale. Buyers discover verified suppliers, compare pricing, place digital orders, and track fulfillment — all in one platform.",
  },
  {
    question: "How does EkkBaz AI help buyers find the best prices?",
    answer:
      "EkkBaz AI continuously analyzes pricing across all suppliers on the platform and provides real-time price benchmarks, alerting buyers when better deals are available and recommending optimal sourcing decisions based on price, quality, and delivery performance.",
  },
  {
    question: "How can suppliers grow their business on EkkBaz's marketplace?",
    answer:
      "Suppliers on EkkBaz gain access to a large network of verified buyers, can list products digitally with real-time inventory and pricing, and receive AI-powered insights on demand trends to optimize their catalog and pricing strategy.",
  },
];
